import { useEffect, useState } from "react";
import { groupDigits } from "./format";

// Live platform stats from the public, unauthenticated backend endpoint
// (GET /api/stats/public/summary/). SSG prerenders with the static i18n fallback
// values; this hook only runs after hydration (useEffect is a no-op during the
// Node prerender), then swaps in the real numbers. On any failure it stays null
// and the caller keeps the static fallback.

export type LiveStats = {
  merchants: number;
  contracts: number;
  managed_volume: number;
  median_create_minutes: number | null;
};

const API_BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ?? "https://api.nasiyah.uz";

export function useLiveStats(): LiveStats | null {
  const [stats, setStats] = useState<LiveStats | null>(null);

  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/api/stats/public/summary/`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: LiveStats | null) => {
        if (alive && d) setStats(d);
      })
      .catch(() => {
        /* silent — keep the static fallback */
      });
    return () => {
      alive = false;
    };
  }, []);

  return stats;
}

/** Integer with uz/ru grouping ("4 200"). Not Intl — see format.ts. */
export function formatCount(n: number): string {
  return groupDigits(Math.round(n));
}

const NB = "\u00a0";

export type VolumeUnit = { div: number; dec: number; word: string };

/** Display unit for a so‘m volume: mlrd (1 decimal), mln, or a plain grouped integer. */
export function volumeUnit(n: number, lang: string): VolumeUnit {
  const ru = lang === "ru";
  if (n >= 1e9) return { div: 1e9, dec: 1, word: ru ? "млрд" : "mlrd" };
  if (n >= 1e6) return { div: 1e6, dec: 0, word: ru ? "млн" : "mln" };
  return { div: 1, dec: 0, word: "" };
}

/**
 * Money volume → "4,2 mlrd" / "12 mln" / grouped integer (no currency word; decimal comma and a
 * non-breaking space, as uz/ru write it). Pass `unit` to keep one unit while a count-up animates.
 */
export function formatVolume(n: number, lang: string, unit: VolumeUnit = volumeUnit(n, lang)): string {
  const v = n / unit.div;
  const num = unit.dec ? v.toFixed(unit.dec).replace(".", ",") : formatCount(v);
  return unit.word ? num + NB + unit.word : num;
}

/** Minutes → rounded "N daqiqa" / "N мин" (unit word is invariant in both langs). */
export function formatMinutes(n: number, lang: string): string {
  return `${Math.max(1, Math.round(n))}${NB}${lang === "ru" ? "мин" : "daqiqa"}`;
}
